import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from "react"
import { supabase } from "../../supabaseClient"
import useMarketIntelligenceCache from "../../hooks/useMarketIntelligenceCache"
import { authHeaders } from "../../utils/authFetch"

export const BRANCHES = [
  {
    branchId: 'e51fe6a0-9247-4f89-bee2-28eee4438069',
    branchName: 'Coffee Gus·to Tierra Vista (Main)',
    latitude: 14.294275526392433, longitude: 120.94514990993164,
    operatingSince: '2018-08-01',
    googlePlaceId: 'ChIJ_QSOIoPVlzMR3RKjEb3zjaA'
  },
  {
    branchId: 'fc0ce422-5be3-401b-8726-893996707196',
    branchName: 'Coffee Gus·to Medicion Imus',
    latitude: 14.445857614454015, longitude: 120.92349527454277,
    operatingSince: '2024-01-01',
    googlePlaceId: 'ChIJtVv8bgDTlzMRxqpY8IlF_CE'
  },
  {
    branchId: '9c474fb7-7a89-4cb4-914b-a959cbd71890',
    branchName: 'Coffee Gus·to Bacoor Blvd',
    latitude: 14.422635000437294, longitude: 120.96589104061741,
    operatingSince: '2022-01-01',
    googlePlaceId: 'ChIJE6C2zibTlzMRTkL-wYs6Yos'
  },
  {
    branchId: '1a19eb85-53aa-4c2e-b249-84720b6a2b43',
    branchName: 'Coffee Gus·to Bella Vista',
    latitude: 14.333602988449089, longitude: 120.91417856760312,
    operatingSince: '2024-01-01',
    googlePlaceId: 'ChIJNURQaQDVlzMRjRk6V0zpLw8'
  },
  {
    branchId: '6d613a63-e26f-4a2b-a676-a01e9daf1873',
    branchName: 'Coffee Gus·to Origins Molino V',
    latitude: 14.3971725322796, longitude: 120.97356368294608,
    operatingSince: '2020-01-01',
    googlePlaceId: 'ChIJQccbwILTlzMRLJVKkygphxs'
  },
  {
    branchId: '7c4c4649-8377-48c5-a1ee-654478cc4bc2',
    branchName: 'Coffee Gus·to DLSUD',
    latitude: 14.320137273251893, longitude: 120.96373480993206,
    operatingSince: '2024-09-21',
    googlePlaceId: 'ChIJwVY5VkfVlzMRf1LY5QB9vTo'
  },
  {
    branchId: 'b5cdd84a-23cb-4244-84c4-5f1c7554e5d6',
    branchName: 'Coffee Gus·to BFRV',
    latitude: 14.44157844210482, longitude: 120.98128135411152,
    operatingSince: '2023-01-01',
    googlePlaceId: 'ChIJsRBKUADTlzMRpOqAu9kfW5U'
  },
  {
    branchId: 'fd75ddad-8fa8-4a1b-8b90-53a5df6f969c',
    branchName: 'Coffee Gus·to Malagasang',
    latitude: 14.374683124010085, longitude: 120.92660165226208,
    operatingSince: '2023-07-14',
    googlePlaceId: 'ChIJB2uDnojTlzMRyrv636G_Nyo'
  },
]

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000
const RANGE_DAYS = { "7d": 7, "30d": 30, "90d": 90, "1y": 365 }

const MarketIntelligenceContext = createContext(null)

export function MarketIntelligenceProvider({ children }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [historyError, setHistoryError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [selectedBranch, setSelectedBranch] = useState(BRANCHES[0])
  const [range, setRange] = useState("30d")

  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const { loadCached, upsertCache } = useMarketIntelligenceCache()

  const resultsByKey = useRef({})

  // ── loadHistory must be declared before generate/refreshFromAPI reference it ──
  const latestBranchIdRef = useRef(selectedBranch.branchId)
  latestBranchIdRef.current = selectedBranch.branchId

  const loadHistory = useCallback(async () => {
    const branchIdAtRequest = selectedBranch.branchId
    setHistoryLoading(true)
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080'
      const res = await fetch(`${API_BASE}/api/market-intelligence/history/${branchIdAtRequest}`, { headers: authHeaders() })
      if (!res.ok) throw new Error('Failed to load history')
      const rows = await res.json()

      if (branchIdAtRequest !== latestBranchIdRef.current) return // stale response — ignore

      const retentionCutoff = Date.now() - THIRTY_DAYS_MS
      const fresh = (rows ?? []).filter(r => new Date(r.generated_at).getTime() >= retentionCutoff)
      setHistory(fresh)
    } catch (err) {
      if (branchIdAtRequest === latestBranchIdRef.current) setHistoryError(err.message)
    } finally {
      if (branchIdAtRequest === latestBranchIdRef.current) setHistoryLoading(false)
    }
  }, [selectedBranch])

  const fetchAndCache = useCallback(async (branch) => {
    // const API_BASE = process.env.REACT_APP_API_URL || 'https://capstone-project-production-62e5.up.railway.app'
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080'

    const statsRes = await fetch(`${API_BASE}/api/branches/${branch.branchId}/stats`, { headers: authHeaders() })
    const stats = await statsRes.json()

    const years = parseFloat(
      ((Date.now() - new Date(branch.operatingSince)) / (1000 * 60 * 60 * 24 * 365)).toFixed(1)
    )

    const response = await fetch(`${API_BASE}/api/intelligence/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({
        ...branch,
        ...stats,
        yearsOperating: years,
        priceRange: "₱90-₱180",
        biggestChallenge: "Growing new customer base beyond existing regulars",
      }),
    })
    const result = await response.json()
    if (!result.success) {
      throw new Error(result.error || "Failed to generate market intelligence.")
    }

    const generatedAt = new Date().toISOString()

    const historyRes = await fetch(`${API_BASE}/api/market-intelligence/history`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({
        branchId: branch.branchId,
        branchName: branch.branchName,
        competitors: result.competitors,
        feedbackSummary: result.feedbackSummary,
        swot: result.swot,
        menuGaps: result.menuGaps,
        kpis: result.kpis,
        generatedAt,
      }),
    })
    if (!historyRes.ok) {
      const body = await historyRes.json().catch(() => ({}))
      throw new Error(body.error || "Failed to save history")
    }

    const { error: cacheError } = await upsertCache(branch.branchId, branch.branchName, result)
    if (cacheError) throw new Error(cacheError.message || String(cacheError))

    return { result, generatedAt }
  }, [upsertCache])

  const generate = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const cached = await loadCached(selectedBranch.branchId)
      const isExpired = cached && cached.generatedAt && (Date.now() - cached.generatedAt.getTime() > THIRTY_DAYS_MS)

      if (cached && !isExpired) {
        const loaded = {
          competitors: cached.competitors,
          feedbackSummary: cached.feedbackSummary,
          swot: cached.swot,
          menuGaps: cached.menuGaps,
          kpis: cached.kpis,
        }
        const updatedAt = cached.generatedAt
        setData(loaded)
        setLastUpdated(updatedAt)
        resultsByKey.current[`${selectedBranch.branchId}__${range}`] = { data: loaded, lastUpdated: updatedAt }
        return
      }

      const { result, generatedAt } = await fetchAndCache(selectedBranch)
      const updatedAt = new Date(generatedAt)
      setData(result)
      setLastUpdated(updatedAt)
      resultsByKey.current[`${selectedBranch.branchId}__${range}`] = { data: result, lastUpdated: updatedAt }
      loadHistory()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [selectedBranch, range, fetchAndCache, loadCached, loadHistory])

  const refreshFromAPI = useCallback(async () => {
    setRefreshing(true)
    setError(null)
    try {
      const { result, generatedAt } = await fetchAndCache(selectedBranch)
      const updatedAt = new Date(generatedAt)
      setData(result)
      setLastUpdated(updatedAt)
      resultsByKey.current[`${selectedBranch.branchId}__${range}`] = { data: result, lastUpdated: updatedAt }
      loadHistory()
    } catch (err) {
      setError(err.message)
    } finally {
      setRefreshing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, fetchAndCache, loadHistory])

  const selectHistoryEntry = useCallback((entry) => {
    const loaded = {
      competitors: entry.competitors,
      feedbackSummary: entry.feedback_summary,
      swot: entry.swot,
      menuGaps: entry.menu_gaps,
      kpis: entry.kpis,
    }
    const updatedAt = new Date(entry.generated_at)
    setData(loaded)
    setLastUpdated(updatedAt)
    resultsByKey.current[`${selectedBranch.branchId}__${range}`] = { data: loaded, lastUpdated: updatedAt }
  }, [selectedBranch, range])

  // History entries that fall within the currently selected date range.
  const rangeFilteredHistory = useMemo(() => {
    const days = RANGE_DAYS[range] ?? 30
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
    return history.filter(
      h => h.branch_id === selectedBranch.branchId && new Date(h.generated_at).getTime() >= cutoff
    )
  }, [history, range, selectedBranch.branchId])

  // Whenever branch OR range changes: restore from in-memory cache if we have it,
  // otherwise auto-pick the most recent snapshot within range, otherwise show empty.
  useEffect(() => {
    const key = `${selectedBranch.branchId}__${range}`
    const cached = resultsByKey.current[key]
    if (cached) {
      setData(cached.data)
      setLastUpdated(cached.lastUpdated)
    } else if (rangeFilteredHistory.length > 0) {
      const latest = rangeFilteredHistory[0]
      const loaded = {
        competitors: latest.competitors,
        feedbackSummary: latest.feedback_summary,
        swot: latest.swot,
        menuGaps: latest.menu_gaps,
        kpis: latest.kpis,
      }
      const updatedAt = new Date(latest.generated_at)
      setData(loaded)
      setLastUpdated(updatedAt)
      resultsByKey.current[key] = { data: loaded, lastUpdated: updatedAt }
    } else {
      setData(null)
      setLastUpdated(null)
    }
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch.branchId, range, rangeFilteredHistory])

  useEffect(() => {
    setHistory([])
    loadHistory()
  }, [selectedBranch.branchId, loadHistory])

  return (
    <MarketIntelligenceContext.Provider
      value={{
        data, loading, refreshing, error, lastUpdated,
        generate, refreshFromAPI,
        selectedBranch, setSelectedBranch,
        range, setRange,
        history: rangeFilteredHistory, historyLoading, loadHistory, selectHistoryEntry,
      }}
    >
      {children}
    </MarketIntelligenceContext.Provider>
  )
}

export function useMarketIntelligence() {
  const ctx = useContext(MarketIntelligenceContext)
  if (!ctx) throw new Error("useMarketIntelligence must be used inside <MarketIntelligenceProvider>")
  return ctx
}